import { Injectable, inject } from '@angular/core';
import { I18nHelper } from './i18n.helper';

export interface CountryItem {
  code: string;
  name: string;
}

/** Most relevant countries for dance events — shown first in dropdown */
const PRIORITY_CODES = [
  'PL', 'ES', 'DE', 'CZ', 'FR', 'IT', 'GB', 'PT', 'NL', 'BE',
  'AT', 'HU', 'SK', 'HR', 'SI', 'RO', 'BG', 'RS', 'GR', 'SE',
  'NO', 'DK', 'FI', 'EE', 'LV', 'LT', 'IE', 'CH', 'LU', 'MT',
  'CY', 'TR', 'UA', 'BY', 'MD', 'AL', 'BA', 'ME', 'MK', 'XK',
  'IS', 'BR', 'CU', 'DO', 'CO', 'MX', 'AR', 'US', 'CA',
];

@Injectable({ providedIn: 'root' })
export class CountryService {
  private readonly i18nHelper = inject(I18nHelper);
  private cache: { locale: string; items: CountryItem[] } | null = null;

  getCountries(): CountryItem[] {
    const locale = this.i18nHelper.currentLang() || 'en';

    if (this.cache && this.cache.locale === locale) {
      return this.cache.items;
    }

    const items = this.buildCountryList(locale);
    this.cache = { locale, items };
    return items;
  }

  getCountryName(countryCode: string): string {
    if (!countryCode) return '';
    try {
      const locale = this.i18nHelper.currentLang() || 'en';
      return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode.toUpperCase()) || countryCode;
    } catch {
      return countryCode;
    }
  }

  private buildCountryList(locale: string): CountryItem[] {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    const prioritySet = new Set(PRIORITY_CODES);

    const priorityCountries: CountryItem[] = [];
    for (const code of PRIORITY_CODES) {
      const name = displayNames.of(code);
      if (name) priorityCountries.push({ code, name });
    }

    const restCountries: CountryItem[] = [];
    for (let i = 65; i <= 90; i++) {
      for (let j = 65; j <= 90; j++) {
        const code = String.fromCharCode(i) + String.fromCharCode(j);
        if (prioritySet.has(code)) continue;
        try {
          const name = displayNames.of(code);
          if (name && name !== code) {
            restCountries.push({ code, name });
          }
        } catch { /* invalid code */ }
      }
    }
    restCountries.sort((a, b) => a.name.localeCompare(b.name, locale));

    return [...priorityCountries, ...restCountries];
  }
}
